import { FindItemsData } from '@/api';
import {
  ApiListResponseBase,
  ApiResponseBase,
  EntityResponseBase,
  PaginatedQueryBase,
} from './common.models';

export const UISelectableItemExportColumnOptions = [
  'id',
  'number',
  'dateAdded',
  'dateUpdated',
  'organization',
  'title',
  'parentNumber',
  'hierarchy',
  'workType',
  'priority',
  'author',
  'team',
  'lane',
  'description_text',
  'description_html',
  // 'description', // hide for now - we don't send this
  'color',
  'pointEffort',
  'childrenPointEffortAggregations',
  'startDate',
  'dueDate',
  'timeSpentSeconds',
  'optimisticEstimate',
  'likelyEstimate',
  'pessimisticEstimate',
  'estimate',
  'childrenEstimateAggregations',
  'estimateSd',
  'isBug',
  'blocker',
  'participants',
  'commentCount',
  'archived',
  'customFields',
  'labels',
  'completedBy',
  'assignee',
  'board',
  'assignedAt',
  'completedAt',
  // Ids
  'organizationId',
  'workTypeId',
  'laneId',
  'teamId',
  'priorityId',
  'boardId',
  'authorUsername',
  'numericItemNumber',
  'assigneeUsername',
  'completedByUser',
] as const;
export const AllItemExportColumnOptions = [
  ...UISelectableItemExportColumnOptions,
  '*',
] as const;

export type ItemExportColumns = (typeof AllItemExportColumnOptions)[number];

export type ExportItemsJobOptions = {
  columns: Array<ItemExportColumns>;
  filters: FindItemsData['query'];
};

export enum ExportJobStatus {
  CREATED = 'created',
  RUNNING = 'running',
  FINISHED = 'finished',
  FAILED = 'failed',
}

export enum ExportJobType {
  ITEMS = 'items',
  ITEM_CYCLE_TRANSACTIONS = 'item_cycle_transactions',
}

export type ExportJob =
  | (EntityResponseBase & {
      organizationId: string;
      name: string;
      userId: string;
      errors: string[];
      status: ExportJobStatus;
    }) & {
      type: ExportJobType.ITEMS;
      options: ExportItemsJobOptions;
    };

export type CreateExportItemsJob = {
  name: string;
  columns: Array<ItemExportColumns>;
  filters: FindItemsData['query'];
};

export enum AnalyticsUICharts {
  CURRENT_PROGRESS = 'current-progress',
  TRENDING_PROGRESS = 'trending-progress',
  THROUGHPUT = 'throughput',
  CUMULATIVE_FLOW = 'cumulative-flow',
  BURNDOWN = 'burndown',
  BURNUP = 'burnup',
  CHURN = 'churn',
  VELOCITY = 'velocity',
  CUSTOM_CYCLE_TIME = 'custom-lead-time',
  USER_TIME_TRACKING = 'user-time-tracking',
}

export enum AnalyticsSummarizeByType {
  TIME_ESTIMATE = 'estimate',
  POINT_ESTIMATE = 'pointEffort',
  COUNT = 'count',
  TIME_SPENT = 'timeSpentSeconds',
}

export enum CurrentProgressChartType {
  COMPLETED = 'completed',
  ASSIGNED = 'assigned',
  PLANNED = 'planned',
  TIME_ESTIMATED = 'time-estimated',
  POINT_ESTIMATED = 'point-estimated',
}

export enum TrendingProgressChartType {
  DONE = 'done',
  TIME_ESTIMATED = 'time-estimated',
  POINT_ESTIMATED = 'point-estimated',
  ASSIGNED = 'assigned',
  PLANNED = 'planned',
  LANE = 'lane',
}

export enum ChurnChartType {
  ADDED_OR_REMOVED_FROM_CYCLE = 'added-or-removed-from-cycle',
  CREATED_OR_DELETED = 'created-or-deleted',
  ASSIGNED_OR_UNASSIGNED = 'assigned-or-unassigned',
  PLANNED_OR_UNPLANNED = 'planned-or-unplanned',
}

export enum TransactionType {
  ITEM_CREATED = 'item_created',
  ITEM_ARCHIVED = 'item_archived',
  ESTIMATION_ADDED = 'estimation_added',
  ESTIMATION_CHANGED = 'estimation_changed',
  ESTIMATION_REMOVED = 'estimation_removed',
  POINT_EFFORT_ADDED = 'point_effort_added',
  POINT_EFFORT_CHANGED = 'point_effort_changed',
  POINT_EFFORT_REMOVED = 'point_effort_removed',
  TIME_SPENT_SECONDS_ADDED = 'time_spent_seconds_added',
  TIME_SPENT_SECONDS_CHANGED = 'time_spent_seconds_changed',
  TIME_SPENT_SECONDS_REMOVED = 'time_spent_seconds_removed',
  ADDED_TO_TEAM = 'added_to_team',
  REMOVED_FROM_TEAM = 'removed_from_team',
  ADDED_TO_LANE = 'added_to_lane',
  REMOVED_FROM_LANE = 'removed_from_lane',
  ADDED_TO_BOARD = 'added_to_board',
  REMOVED_FROM_BOARD = 'removed_from_board',
  ADDED_TO_ASSIGNEE = 'added_to_assignee',
  REMOVED_FROM_ASSIGNEE = 'removed_from_assignee',
}

export type FindExportJobsQueryParams = PaginatedQueryBase;

export type ExportJobResponse = ApiResponseBase<ExportJob>;
export type ExportJobListResponse = ApiListResponseBase<ExportJob>;

export type CubeApiResponse = {
  [key: string]: unknown;
  results: Array<{
    [key: string]: unknown;
    data: Array<{
      [key: string]: string | number | boolean | null | undefined;
    }>;
  }>;
};

export enum ChartOption {
  SERIES_LABELS = 'seriesLabels',
  SERIES_VALUES = 'seriesValues',
  X_AXIS_LABELS = 'xAxisLabels',
  X_AXIS_VALUES = 'xAxisValues',
  Y_AXIS_LABELS = 'yAxisLabels',
  Y_AXIS_VALUES = 'yAxisValues',
}

export type ItemLaneTransaction = {
  'Workitems.title': string;
  'Workitems.number': string;
  'LaneTransactions.transactionType': TransactionType;
  'LaneTransactions.transactionDate': string;
  'LaneTransactions.estimate'?: string;
  'LaneTransactions.pointEffort': '0';
  'LaneTransactions.timeSpentSeconds'?: string;
  'LaneTransactions.laneId'?: string;
  'LaneTransactions.userId'?: string;
  'LaneTransactions.organizationId'?: string;
  'LaneTransactions.itemId'?: string;
  'LaneTransactions.teamId'?: string;
  'LaneTransactions.boardId'?: string;
  'LaneTransactions.assigneeUsername'?: string;
};

export type IItemsTableWorkItem = {
  type: 'item';
  isSummary?: boolean;
  number: string;
  title: string;
  pointEffort: string;
  estimate: string;
  estimateRange: string;
  timeSpentSeconds: string;
  workTypeId: string;
};

export type ITimeSpentWorkItem = {
  type: 'timeSpent';
  isSummary?: boolean;
  workTypeId: string;
  number: string;
  title: string;
  itemTitle?: string;
  description?: string;
  timeSpentSeconds: string;
  timeEntryDate: string;
  completedAt: string;
  authorUsername?: string;
};
export type IItemsTableColumn =
  | {
      id: string;
      label: string;
      valueKey: keyof IItemsTableWorkItem;
    }
  | {
      id: string;
      label: string;
      valueKey: keyof ITimeSpentWorkItem;
    };

export type IItemsTableSection = {
  id: string;
  label: string;
  /** Optional secondary label displayed in a separate column (e.g., hours total) */
  secondaryLabel?: string;
  openOnTableMount?: boolean;
  avatarUrl?: string;
} & (
  | {
      type: 'section';
      data: IItemsTableSection[];
    }
  | {
      type: 'item';
      data:
        | IItemsTableWorkItem[]
        | ((cursor?: string | number) => Promise<{
            data: IItemsTableWorkItem[];
            cursor: string | number | 'not-loaded' | null;
          }>);
    }
  | {
      type: 'item';
      data:
        | ITimeSpentWorkItem[]
        | ((cursor?: string | number) => Promise<{
            data: ITimeSpentWorkItem[];
            cursor: string | number | 'not-loaded' | null;
          }>);
    }
);
